/**
 * 通貨フォーマットのためのパラメータ型定義
 */
type FormatCurrencyParams = {
  amount: number;
} & GetCurrencySymbolParams;

/**
 * 指定された金額を通貨形式でフォーマットし、全角通貨記号(日本円のみ)を半角に変換します。
 */
export const formatCurrencyToObject = ({
  amount,
  currencyCode,
  locale
}: FormatCurrencyParams): {
  sign: string;
  symbol: string;
  amount: string;
} => {
  const trimmedCurrencyCode = currencyCode.trim();

  // 通貨記号を取得
  const symbol = getCurrencySymbol({
    currencyCode: trimmedCurrencyCode,
    locale
  });

  // JPYの場合は小数点以下を表示しない
  const isJPY = trimmedCurrencyCode === 'JPY';

  // 日本円の場合は小数点以下を切り捨てる
  const adjustedAmount = isJPY ? Math.floor(Math.abs(amount)) : Math.abs(amount);

  // 数値部分のフォーマット（通貨記号なし）
  const isIDR = trimmedCurrencyCode === 'IDR';
  /**
   * @see https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
   * @see https://tc39.es/ecma402/#numberformat-objects
   * @note localeを指定しない場合、NumberFormatはブラウザのロケールが使用されるが、インドネシアルピアの場合、常にインドネシアのフォーマットを使用するために`id-ID`を指定する
   */
  const numberFormatter = new Intl.NumberFormat(isIDR ? 'id-ID' : locale, {
    minimumFractionDigits: isJPY || amount === 0 ? 0 : 2,
    maximumFractionDigits: isJPY ? 0 : 2,
    useGrouping: true
  });
  const formattedNumber = numberFormatter.format(adjustedAmount);

  // 負の数の場合、マイナス記号を先頭に付ける
  const sign = amount < 0 ? '-' : '';
  return {
    sign,
    symbol,
    amount: formattedNumber
  };
};

/**
 * テキストで生成するバージョン
 */
export const formatCurrency = ({
  amount,
  currencyCode,
  locale
}: FormatCurrencyParams): string => {
  const {
    sign,
    symbol,
    amount: formattedAmount
  } = formatCurrencyToObject({
    amount,
    currencyCode,
    locale
  });
  return `${sign}${symbol}${formattedAmount}`;
};

/**
 * 通貨シンボル取得のためのパラメータ型定義
 */
type GetCurrencySymbolParams = {
  currencyCode: string;
  locale?: string;
};

/**
 * 通貨コードに対応する通貨シンボルを返します
 *
 * @param {GetCurrencySymbolParams} params - パラメータオブジェクト
 * @returns {string} 通貨シンボル
 */
export const getCurrencySymbol = ({
  currencyCode,
  locale
}: GetCurrencySymbolParams): string => {
  const trimmedCode = currencyCode.trim();

  // 通貨記号を取得するためのフォーマッタ
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: trimmedCode,
    currencyDisplay: 'narrowSymbol'
  });
  const parts = formatter.formatToParts(0);
  const symbolPart = parts.find(part => part.type === 'currency');

  // 通貨記号が見つからない場合は通貨コードを返す
  if (!symbolPart) {
    return trimmedCode;
  }

  // 日本円の場合は全角から半角に変換
  return convertFullWidthToHalfWidth(symbolPart.value);
};

/**
 * 全角円記号を半角に変換します
 */
export const convertFullWidthToHalfWidth = (str: string): string => str.replace('￥', '¥');